import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/context/LanguageContext';
import { getDeviceFingerprint } from '@/utils/visitorFingerprint';

interface TrackingSession {
  sessionId: string;
  startTime: number;
}

interface AnalyticsEvent {
  type: 'exhibit' | 'tour' | 'search';
  action: string;
  entityId?: string;
  metadata?: Record<string, any>;
  duration?: number;
}

export function useAnalyticsTracking() {
  const { language } = useLanguage();
  const sessionRef = useRef<TrackingSession | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const pageStartTime = useRef<number>(Date.now());

  // Initialize session on mount
  useEffect(() => {
    initializeSession();
    return () => {
      // Track session end on unmount
      if (sessionRef.current) {
        updateSessionActivity();
      }
    };
  }, []);

  const initializeSession = async () => {
    try {
      const sessionId = crypto.randomUUID();
      const deviceType = getDeviceType();
      const { screenResolution, timezone, userAgent } = getDeviceFingerprint();

      // Generate visitor fingerprint and check if returning visitor
      let visitorFingerprint: string | null = null;
      let isReturningVisitor = false;

      try {
        const { data: fingerprintData } = await supabase.rpc('generate_visitor_fingerprint', {
          p_ip_address: null, // IP will be handled server-side
          p_user_agent: userAgent,
          p_screen_resolution: screenResolution,
          p_timezone: timezone
        });

        if (fingerprintData) {
          visitorFingerprint = fingerprintData;
          
          const { data: isReturning } = await supabase.rpc('check_returning_visitor', {
            p_fingerprint: fingerprintData
          });
          
          isReturningVisitor = isReturning || false;
        }
      } catch (fingerprintError) {
        console.warn('Failed to generate visitor fingerprint, continuing with basic tracking:', fingerprintError);
      }

      const { error } = await supabase
        .from('user_sessions')
        .insert({
          session_id: sessionId,
          language_preference: language,
          device_type: deviceType,
          user_agent: userAgent,
          visitor_fingerprint: visitorFingerprint,
          screen_resolution: screenResolution,
          timezone: timezone,
          is_returning_visitor: isReturningVisitor
        });

      if (error) {
        console.error('Failed to initialize analytics session:', error);
        return;
      }

      sessionRef.current = {
        sessionId,
        startTime: Date.now()
      };

      setIsInitialized(true);
    } catch (error) {
      console.error('Analytics initialization error:', error);
    }
  };

  const updateSessionActivity = async () => {
    if (!sessionRef.current) return;

    try {
      await supabase
        .from('user_sessions')
        .update({ 
          last_activity: new Date().toISOString(),
          language_preference: language
        })
        .eq('session_id', sessionRef.current.sessionId);
    } catch (error) {
      console.error('Failed to update session activity:', error);
    }
  };

  const trackEvent = async (event: AnalyticsEvent) => {
    if (!sessionRef.current || !isInitialized) return;

    try {
      const baseData = {
        session_id: sessionRef.current.sessionId,
        language_used: language,
        metadata: event.metadata || {}
      };

      switch (event.type) {
        case 'exhibit':
          await supabase
            .from('exhibit_interactions')
            .insert({
              ...baseData,
              exhibit_id: event.entityId!,
              interaction_type: event.action,
              duration_seconds: event.duration
            });
          break;

        case 'tour':
          await supabase
            .from('tour_interactions')
            .insert({
              ...baseData,
              tour_id: event.entityId!,
              interaction_type: event.action,
              progress_percentage: event.metadata?.progress
            });
          break;

        case 'search':
          await supabase
            .from('search_interactions')
            .insert({
              ...baseData,
              search_query: event.metadata?.query || '',
              search_type: event.action,
              results_found: event.metadata?.resultsFound || 0,
              selected_result_id: event.entityId,
              selected_result_type: event.metadata?.resultType
            });
          break;
      }

      // Update session activity
      updateSessionActivity();
    } catch (error) {
      console.error('Failed to track analytics event:', error);
    }
  };

  // Tracking methods for different types of interactions
  const trackExhibitView = (exhibitId: string, duration?: number) => {
    trackEvent({
      type: 'exhibit',
      action: 'view',
      entityId: exhibitId,
      duration
    });
  };

  const trackExhibitFavorite = (exhibitId: string, action: 'add' | 'remove') => {
    trackEvent({
      type: 'exhibit',
      action: action === 'add' ? 'favorite_add' : 'favorite_remove',
      entityId: exhibitId
    });
  };

  const trackAudioPlay = (exhibitId: string, completed: boolean = false) => {
    trackEvent({
      type: 'exhibit',
      action: completed ? 'audio_complete' : 'audio_play',
      entityId: exhibitId
    });
  };

  const trackImageView = (exhibitId: string) => {
    trackEvent({
      type: 'exhibit',
      action: 'image_view',
      entityId: exhibitId
    });
  };

  const trackTourView = (tourId: string) => {
    trackEvent({
      type: 'tour',
      action: 'view',
      entityId: tourId
    });
  };

  const trackTourStart = (tourId: string) => {
    trackEvent({
      type: 'tour',
      action: 'start',
      entityId: tourId
    });
  };

  const trackTourProgress = (tourId: string, progress: number) => {
    trackEvent({
      type: 'tour',
      action: 'exhibit_visit',
      entityId: tourId,
      metadata: { progress }
    });
  };

  const trackSearch = (query: string, type: 'number_search' | 'text_search' | 'qr_scan', resultsFound: number, selectedResultId?: string, resultType?: 'exhibit' | 'tour') => {
    trackEvent({
      type: 'search',
      action: type,
      entityId: selectedResultId,
      metadata: {
        query,
        resultsFound,
        resultType
      }
    });
  };

  return {
    isInitialized,
    trackExhibitView,
    trackExhibitFavorite,
    trackAudioPlay,
    trackImageView,
    trackTourView,
    trackTourStart,
    trackTourProgress,
    trackSearch,
    updateSessionActivity
  };
}

function getDeviceType(): 'mobile' | 'desktop' | 'tablet' {
  const userAgent = navigator.userAgent;
  
  if (/iPad|Android(?=.*\bTablet\b)/.test(userAgent)) {
    return 'tablet';
  }
  
  if (/Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    return 'mobile';
  }
  
  return 'desktop';
}